/** Schlüssel der Erinnerungspause im localStorage. Die Pause hängt am
 *  Browser, nicht am Benutzer — wer sich ein Browserprofil teilt, teilt sie. */
export const SNOOZE_STORAGE_KEY = 'notification_startup_cooldown';

/** Event, mit dem die Glocke anderen Masken im selben Tab mitteilt, dass der
 *  Server geänderte Themen-Abos geschoben hat. Die Benachrichtigungsmaske in
 *  „Mein Konto" liest daraufhin neu, statt einen veralteten Stand zu zeigen. */
export const USER_TOPICS_CHANGED_EVENT = 'dino:usertopics-changed';

export interface ITopicSubscriptionDiff {
  toSubscribe: number[];
  toUnsubscribe: number[];
}

/** Vergleicht die tatsächlich abonnierten Themen mit den gewünschten.
 *
 *  Bewusst gegen die *tatsächlich* registrierten Listener statt gegen den
 *  vorherigen Server-Stand: sonst laufen Zustand und Socket-Listener
 *  auseinander, sobald ein Push verloren geht oder zweimal ankommt — ein
 *  wieder eingeschaltetes Thema bliebe dann stumm (QS #18, Testfall 4).
 */
export const diffTopicSubscriptions = (
  subscribed: number[],
  desired: number[]
): ITopicSubscriptionDiff => {
  const subscribedSet = new Set(subscribed);
  const desiredSet = new Set(desired);
  const unique = (topics: number[]) =>
    topics.filter((topic, index) => topics.indexOf(topic) === index);

  return {
    toSubscribe: unique(desired).filter(topic => !subscribedSet.has(topic)),
    toUnsubscribe: unique(subscribed).filter(topic => !desiredSet.has(topic)),
  };
};

/** Datum, bis zu dem das Fenster „Ungelesene Benachrichtigungen" ausbleibt —
 *  als `YYYY-MM-DD`, weil auf Tagesbasis verglichen wird. */
export const snoozeUntil = (days: number, from: Date = new Date()): string => {
  const until = new Date(from);
  until.setHours(0, 0, 0, 0);
  until.setDate(until.getDate() + days);
  const month = String(until.getMonth() + 1).padStart(2, '0');
  const day = String(until.getDate()).padStart(2, '0');
  return `${until.getFullYear()}-${month}-${day}`;
};

/** Ist die gespeicherte Erinnerungspause abgelaufen?
 *
 *  Fehlt der Wert oder ist er unlesbar, gilt die Pause als abgelaufen — ein
 *  kaputter localStorage-Eintrag darf das Fenster nicht dauerhaft und
 *  unerklärlich unterdrücken.
 */
export const isSnoozeOver = (
  storedValue: string | null,
  today: Date = new Date()
): boolean => {
  if (!storedValue) return true;

  const until = new Date(storedValue);
  if (isNaN(until.getTime())) return true;

  const reference = new Date(today);
  reference.setHours(0, 0, 0, 0);
  until.setHours(0, 0, 0, 0);

  return until <= reference;
};
